import { useState, useEffect } from 'react';

export interface Album {
  type: 'album';
  albumId: string;
  slug: string;
  title: string;
  behaviorContext: string[];
  soundMode: string[];
}

export interface Track {
  type: 'track';
  trackId: string;
  albumId: string;
  albumSlug: string;
  n: number;
  title: string;
  bpm: number;
  paths: {
    mp3: string;
    wav: string;
  };
}

type LibraryItem = Album | Track;

export function useMusicLibrary() {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadLibrary() {
      try {
        const response = await fetch('/library.ndjson');

        if (!response.ok) {
          throw new Error(
            `Failed to load music library: ${response.statusText}`
          );
        }

        const text = await response.text();
        const lines = text.trim().split('\n');
        const items: LibraryItem[] = lines.map(line => JSON.parse(line));

        const albumsData = items.filter(
          (item): item is Album => item.type === 'album'
        );
        const tracksData = items.filter(
          (item): item is Track => item.type === 'track'
        );

        setAlbums(albumsData);
        setTracks(tracksData);
        setLibraryError(null);
      } catch (error) {
        console.error('Error loading library:', error);
        setLibraryError(
          'Failed to load music library. Please refresh the page.'
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadLibrary();
  }, []);

  return { albums, tracks, libraryError, isLoading };
}
